import AvatarGroup from '@atlaskit/avatar-group';
import Lozenge from '@atlaskit/lozenge';
import { colors, elevation } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';
import React from 'react';
import styled from 'styled-components';

import { Collaborator } from '../../../clients/identityClient';
import { redirect } from '../../../facade/window';
import { AnalyticsConsumerProps, withAnalytics } from '../../../hoc/withAnalytics';
import { PRODUCT_TYPE } from '../../../model/productType';
import { Product } from '../../../store/productStore';
import { isNewTabClick } from '../../../util/event';
import { ellipsis } from '../../styled/text';
import { cardContainerStyle } from '../style';

import ProductIcon, { Container as IconContainer } from './ProductIcon';

export interface JoinableProduct extends Product {
  avatars?: string[];
  className?: string;
  collaborators: Collaborator[];
  fullScreen?: boolean;
  hideSiteName?: boolean;
  productKey: PRODUCT_TYPE;
  tabIndex?: number;
}

interface AvatarData {
  key: string;
  name: string;
  src?: string;
}

type Props = JoinableProduct;

const Container = styled.a`
  ${cardContainerStyle};

  &:hover {
    > ${IconContainer} {
      ${elevation.e400};
    }
  }
`;

export const ProductName = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${colors.N800};
`;

const SiteName = styled.div`
  color: ${colors.N100};
  font-size: 12px;
  ${ellipsis};
`;

class ProductCard extends React.Component<Props & AnalyticsConsumerProps> {
  render() {
    const { className, collaborators, icon, productName, siteName, url, hideSiteName } = this.props;

    let avatarData: AvatarData[] = [];
    if (collaborators && collaborators.length) {
      avatarData = collaborators.map((collaborator: Collaborator, index: number) => ({
        key: `${index}`,
        name: collaborator.displayName,
        src: collaborator.avatarUrl,
      }));
    }
    return (
      <Container className={className} href={url} onClick={this.onClick} tabIndex={this.props.tabIndex}>
        <ProductIcon icon={icon} />
        <ProductName>{productName}</ProductName>
        {!hideSiteName && (
          <Tooltip content={siteName} delay={1000} hideTooltipOnMouseDown>
            <SiteName data-test-id="sitename">{siteName}</SiteName>
          </Tooltip>
        )}
        {collaborators.length > 0 && (
          <span
            style={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              height: '30px',
              justifyContent: 'start',
            }}
          >
            <AvatarGroup
              size="small"
              maxCount={3}
              data={avatarData}
              appearance="stack"
              onAvatarClick={this.onAvatarClick}
              onMoreClick={this.cancelClickEvent}
            />
          </span>
        )}
        <Lozenge appearance="new">Join</Lozenge>
      </Container>
    );
  }

  private cancelClickEvent = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
  };

  private onAvatarClick = ({ event }: { event: React.MouseEvent<HTMLElement> }) => this.cancelClickEvent(event);

  private onClick = async (event: React.MouseEvent<HTMLElement>) => {
    const { cloudId, fullScreen, productName, siteName, url } = this.props;
    const isOpeningInNewTab = isNewTabClick(event);
    if (!isOpeningInNewTab) {
      event.preventDefault();
    }
    await this.props.analytics.ui('productCard', 'clicked', {
      cloudId,
      fullScreen,
      joinable: true,
      product: productName,
      siteName,
      url,
    });
    if (!isOpeningInNewTab) {
      redirect(url);
    }
  };
}

export default withAnalytics(ProductCard);
